import type { SubmitData, SubmitActions, PaymentAction, Core, UIElement, CardConfiguration } from '@adyen/adyen-web';
import { ADYEN_CARD_CONFIG, ADYEN_CARD_FIELD, ADYEN_DATE_FIELD } from '@/constants/adyen';
import { createAdyenConfiguration } from '@/utils/adyen';
import type {
  AdyenConfig,
  AdyenCardComponent,
  CardDetails,
  CardFieldEvent,
  BinValueEvent,
  CallbackActions,
} from '@/types/adyen';
import type { Customer } from '@/types/checkout';

class AdyenService {
  private checkoutInstance: Core | null = null;
  private isInitializing = false;
  private onPaymentSuccess: (params: unknown) => void = () => {};
  private onPaymentError: () => void = () => {};
  private onPaymentFailed: () => void = () => {};

  setOnPaymentFailed(onPaymentFailed: () => void): void {
    this.onPaymentFailed = onPaymentFailed;
  }

  runPaymentFailed(): void {
    this.onPaymentFailed?.();
  }

  updateOnPaymentSuccess(onPaymentSuccess: (params: unknown) => void): void {
    this.onPaymentSuccess = onPaymentSuccess;
  }

  updateOnPaymentError(onPaymentError: () => void): void {
    this.onPaymentError = onPaymentError;
  }

  callOnPaymentSuccess(result: unknown): void {
    this.onPaymentSuccess(result);
  }

  callOnPaymentError(): void {
    this.onPaymentError();
  }

  async initialize(config: AdyenConfig, customer?: Customer): Promise<Core> {
    if (this.checkoutInstance) {
      return this.checkoutInstance;
    }

    if (this.isInitializing) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      return this.initialize(config, customer);
    }

    this.isInitializing = true;

    try {
      const configuration = createAdyenConfiguration(config, customer);

      if (!configuration.environment) {
        throw new Error('Adyen environment is required (test or live)');
      }

      const adyenWeb = await import('@adyen/adyen-web');
      const AdyenCheckoutClass = adyenWeb.AdyenCheckout || (adyenWeb as Record<string, unknown>).default;

      if (!AdyenCheckoutClass || typeof AdyenCheckoutClass !== 'function') {
        throw new Error('Failed to import AdyenCheckout');
      }

      const instance = await AdyenCheckoutClass(configuration);
      this.checkoutInstance = instance;
      return this.checkoutInstance;
    } finally {
      this.isInitializing = false;
    }
  }

  async createFromAction(action: PaymentAction, callbacks: CallbackActions): Promise<unknown> {
    try {
      return (
        this.checkoutInstance &&
        this.checkoutInstance.createFromAction(action, {
          ...ADYEN_CARD_CONFIG,
          ...callbacks,
        })
      );
    } catch (error) {
      console.error('Failed to create from action:', error);
      return null;
    }
  }

  async createCardComponent(
    container: HTMLElement,
    handlers: {
      onSubmit: (state: SubmitData, component: unknown, actions: SubmitActions) => void;
      onError: () => void;
      onFieldValid: (event: CardFieldEvent) => void;
      onBinValue: (event: BinValueEvent) => void;
    },
    configOverrides: Partial<CardConfiguration> = {}
  ): Promise<AdyenCardComponent | null> {
    if (!this.checkoutInstance) return null;

    try {
      const { Card } = await import('@adyen/adyen-web');

      const component = new Card(this.checkoutInstance as unknown as never, {
        ...ADYEN_CARD_CONFIG,
        ...configOverrides,
        ...handlers,
        onPaymentCompleted: () => {
          this.onPaymentSuccess(null);
        },
        onPaymentFailed: (_, component: UIElement) => {
          component?.setStatus('ready');
          this.onPaymentError();
        },
      });
      component.mount(container);
      return component as unknown as AdyenCardComponent;
    } catch (error) {
      console.error('Failed to create card component:', error);
      return null;
    }
  }

  reset(): void {
    this.checkoutInstance = null;
    this.isInitializing = false;
  }
}

export const adyenService = new AdyenService();

export const createCardFieldHandlers = (
  cardDetailsRef: { current: CardDetails },
  _: { current: AdyenCardComponent | undefined },
  onError: () => void
) => ({
  onError,
  onFieldValid: (event: CardFieldEvent) => {
    const { fieldType, valid, endDigits, expiryDate } = event;
    if (fieldType === ADYEN_CARD_FIELD && valid && endDigits) {
      cardDetailsRef.current.endDigits = endDigits;
    } else if (fieldType === ADYEN_DATE_FIELD && valid && expiryDate) {
      cardDetailsRef.current.expiryDate = expiryDate;
    }
  },
  onBinValue: (event: BinValueEvent) => {
    const { binValue, brand } = event;
    if (binValue) {
      cardDetailsRef.current.bin = binValue;
    }
    if (brand) {
      cardDetailsRef.current.cardType = brand;
    }
  },
});
